import { format, isValid, parse, parseISO, startOfDay } from "date-fns";
import {
  formatDisplayTime,
  parseApiTimeValue,
  resolvePharmacyTimeFormat,
} from "./timeFormat";

/** Read currentPharmacyPOS for display helpers without importing PharmacyProvider (avoids cycles). */
const readPharmacyPOSForFormats = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    const saved = localStorage.getItem("currentPharmacyPOS");
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

/** Fallback until the pharmacy date format is provided after login/API. */
export const DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT = "dd MMM yyyy";

/** Storage / login token form of the pharmacy default date format. */
export const DEFAULT_PHARMACY_DATE_FORMAT_TOKEN = "DD MMM YYYY";

/**
 * Canonical API / form-storage date format.
 * UI may show any pharmacy date format; values emitted to forms/APIs must use this.
 */
export const API_DATE_FORMAT = "yyyy-MM-dd";

/** @deprecated Use API_DATE_FORMAT — kept as an alias for existing imports. */
export const DEFAULT_OUTPUT_DATE_FORMAT = API_DATE_FORMAT;

/**
 * Supported pharmacy Settings date formats (API / UI token form).
 * Used to reject null/empty/invalid stored values with the default fallback.
 */
export const SUPPORTED_PHARMACY_DATE_FORMATS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD.MM.YYYY",
  "YYYY.MM.DD",
  "DD MMM YYYY",
  "MMM DD, YYYY",
];

/**
 * Normalize common UI date-format tokens (DD/MM/YYYY) to date-fns tokens (dd/MM/yyyy).
 * Keeps already-normalized date-fns formats unchanged.
 */
export const normalizeDateFormat = (dateFormat) => {
  if (!dateFormat) return DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT;
  let next = String(dateFormat).trim();
  if (!next) return DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT;

  next = next.replace(/YYYY/g, "yyyy");
  next = next.replace(/YY/g, "yy");
  next = next.replace(/DD/g, "dd");
  // Remaining single D (day) → d; do not touch already-normalized "dd"
  next = next.replace(/D/g, "d");
  return next;
};

/** True when the value maps to a supported pharmacy display format. */
export const isSupportedPharmacyDateFormat = (dateFormat) => {
  if (dateFormat == null || String(dateFormat).trim() === "") return false;
  const normalized = normalizeDateFormat(dateFormat);
  return SUPPORTED_PHARMACY_DATE_FORMATS.some(
    (fmt) => normalizeDateFormat(fmt) === normalized
  );
};

/**
 * First non-empty date_format candidate from pharmacy / nested currency.
 */
export const pickPharmacyDateFormatRaw = (pharmacyPOS) => {
  if (!pharmacyPOS || typeof pharmacyPOS !== "object") return null;
  const candidates = [
    pharmacyPOS.date_format,
    pharmacyPOS.dateFormat,
    pharmacyPOS.currency?.date_format,
    pharmacyPOS.currency?.dateFormat,
    pharmacyPOS.preferred_date_format,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    if (String(candidate).trim() === "") continue;
    return candidate;
  }
  return null;
};

/**
 * Resolve the pharmacy's preferred display date format.
 * Reads login/localStorage field names (e.g. pharmacyPOS.date_format).
 * Missing, empty, or unsupported values fall back to DD MMM YYYY.
 */
export const resolvePharmacyDateFormat = (pharmacyPOS) => {
  const raw = pickPharmacyDateFormatRaw(pharmacyPOS);
  if (raw == null) return DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT;
  if (!isSupportedPharmacyDateFormat(raw)) return DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT;
  return normalizeDateFormat(raw);
};

/**
 * Normalize a login/API date_format into the allow-list token for storage
 * (e.g. "DD MMM YYYY"). Empty/invalid → "".
 */
export const normalizeStoredPharmacyDateFormat = (value) => {
  if (value == null || String(value).trim() === "") return "";
  const raw = String(value).trim();
  if (SUPPORTED_PHARMACY_DATE_FORMATS.includes(raw)) return raw;
  const normalized = normalizeDateFormat(raw);
  const match = SUPPORTED_PHARMACY_DATE_FORMATS.find(
    (fmt) => normalizeDateFormat(fmt) === normalized
  );
  return match || "";
};

/**
 * DatePicker display format priority:
 * Explicit prop → pharmacy stored format → existing default.
 * Empty/whitespace props are treated as missing.
 * Explicit props are normalized but not restricted to the settings allow-list.
 */
export const resolvePickerDateFormat = (propFormat, pharmacyPOS) => {
  const explicit =
    propFormat != null && String(propFormat).trim() !== ""
      ? String(propFormat).trim()
      : null;
  if (explicit) return normalizeDateFormat(explicit);
  return resolvePharmacyDateFormat(pharmacyPOS);
};

const coerceToDate = (value, formats = []) => {
  if (!value && value !== 0) return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value).trim();
  if (!text) return null;

  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const iso = parseISO(text);
    if (isValid(iso)) return iso;
  }

  for (const pattern of formats) {
    if (!pattern) continue;
    const parsed = parse(text, normalizeDateFormat(pattern), new Date());
    if (isValid(parsed)) return parsed;
  }

  const fallback = new Date(text);
  return isValid(fallback) ? fallback : null;
};

/** Parse a controlled value (Date | string) into a Date for the picker. */
export const parseDateValue = (value, formats = []) =>
  coerceToDate(value, formats);

/** Format a Date for form state / API payloads (always YYYY-MM-DD). */
export const formatDateValue = (date) => {
  if (!(date instanceof Date) || !isValid(date)) return "";
  return format(date, API_DATE_FORMAT);
};

/** Format a Date for display using the configured pharmacy date format. */
export const formatDisplayDate = (date, dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  if (!(date instanceof Date) || !isValid(date)) return "";
  return format(date, normalizeDateFormat(dateFormat));
};

/** Resolved pharmacy display date format from `currentPharmacyPOS` (fallback DD MMM YYYY). */
export const getPharmacyDisplayDateFormat = () =>
  resolvePharmacyDateFormat(readPharmacyPOSForFormats());

/**
 * Parse a display/API date value into a local Date.
 * Prefer YYYY-MM-DD as calendar-local to avoid UTC day shifts.
 */
export const parsePharmacyDisplayDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isValid(value) ? value : null;

  const text = String(value).trim();
  if (!text) return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]) - 1;
    const day = Number(dateOnly[3]);
    const local = new Date(year, month, day);
    return isValid(local) ? local : null;
  }

  return coerceToDate(text, [API_DATE_FORMAT, DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT]);
};

/**
 * Format any date-like value for UI using pharmacy `date_format` from currentPharmacyPOS.
 * Empty/invalid → `empty` (default "—"). API/storage remains YYYY-MM-DD elsewhere.
 */
export const formatPharmacyDate = (value, empty = "—") => {
  const date = parsePharmacyDisplayDate(value);
  if (!date) return empty;
  const formatted = formatDisplayDate(date, getPharmacyDisplayDateFormat());
  return formatted || empty;
};

/**
 * Format ISO/datetime for UI using pharmacy date + time formats.
 * Empty/invalid → `empty` (default "—").
 */
export const formatPharmacyDateTime = (value, empty = "—") => {
  const parts = formatPharmacyDateTimeParts(value, empty);
  if (!parts || parts.date === empty || !parts.date) return empty;
  if (!parts.time) return parts.date;
  return `${parts.date}, ${parts.time}`;
};

/**
 * Split ISO/datetime into pharmacy-formatted date and time parts for two-line tables.
 */
export const formatPharmacyDateTimeParts = (value, empty = "—") => {
  if (value == null || value === "") return { date: empty, time: null };

  let date = null;
  if (value instanceof Date) {
    date = isValid(value) ? value : null;
  } else {
    const text = String(value).trim();
    if (!text) return { date: empty, time: null };
    // Full timestamps: use native Date so local hours/minutes are preserved.
    if (/^\d{4}-\d{2}-\d{2}T/.test(text) || text.includes(" ")) {
      const parsed = new Date(text);
      date = isValid(parsed) ? parsed : null;
    } else {
      date = parsePharmacyDisplayDate(text);
    }
  }

  if (!date) return { date: empty, time: null };

  const dateLabel = formatDisplayDate(date, getPharmacyDisplayDateFormat()) || empty;
  const timeLabel = formatDisplayTime(date, resolvePharmacyTimeFormat(readPharmacyPOSForFormats()));
  return { date: dateLabel, time: timeLabel || null };
};

/**
 * True when `date` is the same calendar day as `today` (day + month + year).
 * Used for Today highlighting — never compare day-of-month alone.
 */
export const isCalendarToday = (date, today = new Date()) => {
  if (!(date instanceof Date) || !isValid(date)) return false;
  if (!(today instanceof Date) || !isValid(today)) return false;
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

/** Parse min/max boundaries passed as Date or string. */
export const parseBoundaryDate = (value) =>
  coerceToDate(value, [API_DATE_FORMAT, DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT]);

/**
 * Tokenize a date-fns format into groups and separators.
 * Example: dd/MM/yyyy → num/sep/num/sep/num
 * Example: dd MMM yyyy → num/sep/monthName/sep/num
 * Longest tokens first so MMM is not split into MM + M.
 */
export const getDateFormatStructure = (dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  const normalized = normalizeDateFormat(dateFormat);
  const parts = [];
  // MMMM/MMM before MM/M; yyyy before yy; dd before d
  const tokenRegex = /yyyy|yy|MMMM|MMM|MM|dd|M|d|[^dyM]+/g;
  let match;

  while ((match = tokenRegex.exec(normalized)) !== null) {
    const token = match[0];
    if (/^(yyyy|yy|MM|dd|M|d)$/.test(token)) {
      const length =
        token === "yyyy" ? 4 : token === "yy" ? 2 : token === "M" || token === "d" ? 2 : token.length;
      parts.push({ kind: "num", token, length });
    } else if (token === "MMM" || token === "MMMM") {
      parts.push({
        kind: "monthName",
        token,
        length: token === "MMMM" ? 9 : 3, // longest English month / abbr
      });
    } else {
      parts.push({ kind: "sep", value: token });
    }
  }

  return parts;
};

/** Whether the format uses a textual month (MMM / MMMM). */
export const dateFormatHasMonthName = (dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) =>
  getDateFormatStructure(dateFormat).some((part) => part.kind === "monthName");

/** Total digit slots implied by the format (e.g. dd/MM/yyyy → 8; dd MMM yyyy → 6). */
export const getDateFormatDigitCount = (dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) =>
  getDateFormatStructure(dateFormat)
    .filter((part) => part.kind === "num")
    .reduce((sum, part) => sum + part.length, 0);

/** Whether typed/pasted text already has all required slots for the format. */
export const isDateInputDigitComplete = (
  text,
  dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT
) => {
  const raw = String(text ?? "").trim();
  if (!raw) return false;

  const structure = getDateFormatStructure(dateFormat);
  const neededDigits = structure
    .filter((part) => part.kind === "num")
    .reduce((sum, part) => sum + part.length, 0);
  const digits = raw.replace(/\D/g, "");
  if (digits.length < neededDigits) return false;

  const monthNameParts = structure.filter((part) => part.kind === "monthName");
  if (!monthNameParts.length) return true;

  // MMM requires 3 letters; do not treat digit-only input as complete.
  const letters = raw.replace(/[^A-Za-z]/g, "");
  const neededLetters = monthNameParts.reduce(
    (sum, part) => sum + (part.token === "MMMM" ? 3 : 3),
    0
  );
  return letters.length >= neededLetters;
};

/** English month abbreviations (Jan–Dec) for MMM formats. */
const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const titleCaseMonthToken = (letters) => {
  const raw = String(letters || "");
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

/**
 * Replace numeric MM tokens without corrupting textual MMM/MMMM months.
 * Temporarily shields month-name tokens, then restores them.
 */
const replaceNumericMonthToken = (pattern, replacement) => {
  const shielded = String(pattern)
    .replace(/MMMM/g, "\u0000")
    .replace(/MMM/g, "\u0001");
  return shielded
    .replace(/MM/g, replacement)
    .replace(/\u0000/g, "MMMM")
    .replace(/\u0001/g, "MMM");
};

/** Max visible characters for the masked input (padded format length). */
export const getDateFormatMaxLength = (dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  const normalized = normalizeDateFormat(dateFormat);
  if (!dateFormatHasMonthName(normalized)) return normalized.length;

  // Build capacity from structure so MMM letter slots are never under-counted.
  return getDateFormatStructure(normalized).reduce((sum, part) => {
    if (part.kind === "sep") return sum + part.value.length;
    if (part.kind === "monthName") return sum + (part.token === "MMM" ? 3 : 9);
    return sum + part.length;
  }, 0);
};

/**
 * Placeholder shown in the input, e.g. dd/MM/yyyy → DD/MM/YYYY.
 * Derived from the configured format — never hardcoded to one pattern.
 * Month-name formats keep MMM/MMMM (e.g. dd MMM yyyy → DD MMM YYYY).
 */
export const getDateFormatPlaceholder = (dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  const normalized = normalizeDateFormat(dateFormat);
  // Shield textual months so numeric MM/M replacements cannot corrupt them.
  return normalized
    .replace(/MMMM/g, "\u0000")
    .replace(/MMM/g, "\u0001")
    .replace(/yyyy/g, "YYYY")
    .replace(/yy/g, "YY")
    .replace(/MM/g, "MM")
    .replace(/dd/g, "DD")
    .replace(/M/g, "M")
    .replace(/d/g, "D")
    .replace(/\u0000/g, "MMMM")
    .replace(/\u0001/g, "MMM");
};

/**
 * True when text looks like a date-format guide (e.g. DD-MM-YYYY), not a field label.
 */
export const isDateFormatGuidePlaceholder = (text) => {
  const raw = String(text ?? "").trim();
  if (!raw) return false;
  // Numeric 3-part guides with matching separators.
  if (
    /^(YYYY|YY|DD|D|MM|M)([./\-])(YYYY|YY|DD|D|MM|M)\2(YYYY|YY|DD|D|MM|M)$/i.test(
      raw
    )
  ) {
    return true;
  }
  // Textual-month guides (e.g. DD MMM YYYY).
  return /^(YYYY|YY|DD|D|MMMM|MMM|MM|M)([\s./\-])(YYYY|YY|DD|D|MMMM|MMM|MM|M)\2(YYYY|YY|DD|D|MMMM|MMM|MM|M)$/i.test(
    raw
  );
};

/**
 * Resolve the DatePicker input placeholder.
 * Priority: format-like explicit prop → placeholder from resolved date format.
 * Field labels such as "From Date" / "Date of Birth" are ignored.
 */
export const resolveDatePickerPlaceholder = (propPlaceholder, dateFormat) => {
  const formatPlaceholder = getDateFormatPlaceholder(dateFormat);
  if (isDateFormatGuidePlaceholder(propPlaceholder)) {
    return String(propPlaceholder).trim();
  }
  return formatPlaceholder;
};

/**
 * Guide typed/pasted input into the configured format:
 * - numeric formats: digits + auto-inserted separators
 * - month-name formats (MMM): digits + letters + separators
 * - supports Backspace/Delete without fighting the mask
 */
export const maskDateInput = (
  rawInput,
  dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT,
  previousMasked = ""
) => {
  const structure = getDateFormatStructure(dateFormat);
  if (!structure.length) return "";

  const previous = String(previousMasked || "");
  const incoming = String(rawInput || "");
  const isDeleting = incoming.length < previous.length;
  const hasMonthName = structure.some((part) => part.kind === "monthName");

  if (!hasMonthName) {
    const maxDigits = getDateFormatDigitCount(dateFormat);
    const digits = incoming.replace(/\D/g, "").slice(0, maxDigits);

    let result = "";
    let digitIndex = 0;

    for (let i = 0; i < structure.length; i += 1) {
      const part = structure[i];
      if (part.kind !== "num") continue;

      if (digitIndex >= digits.length) break;

      const slice = digits.slice(digitIndex, digitIndex + part.length);
      result += slice;
      digitIndex += slice.length;

      const groupComplete = slice.length === part.length;
      const next = structure[i + 1];
      if (!groupComplete || !next || next.kind !== "sep") continue;

      const hasMoreDigits = digitIndex < digits.length;
      // While typing forward, auto-insert the format separator after a complete group.
      // While deleting, only keep a separator when more digits still follow it.
      if (hasMoreDigits || !isDeleting) {
        result += next.value;
      }
    }

    return result;
  }

  // Month-name formats: walk structure, consume digits/letters from a sanitized stream.
  const source = incoming.replace(/[^0-9A-Za-z]/g, "");
  let srcIndex = 0;
  let result = "";

  for (let i = 0; i < structure.length; i += 1) {
    const part = structure[i];

    if (part.kind === "sep") {
      // Separators are inserted after groups, not consumed from source.
      continue;
    }

    if (part.kind === "num") {
      let slice = "";
      while (slice.length < part.length && srcIndex < source.length) {
        const ch = source[srcIndex];
        if (/\d/.test(ch)) {
          slice += ch;
          srcIndex += 1;
        } else {
          break;
        }
      }
      if (!slice) break;
      result += slice;

      const groupComplete = slice.length === part.length;
      const next = structure[i + 1];
      if (groupComplete && next?.kind === "sep") {
        const hasMore = srcIndex < source.length;
        if (hasMore || !isDeleting) {
          result += next.value;
        }
      }
      continue;
    }

    if (part.kind === "monthName") {
      const maxLetters = part.token === "MMM" ? 3 : 9;
      let letters = "";
      while (letters.length < maxLetters && srcIndex < source.length) {
        const ch = source[srcIndex];
        if (/[A-Za-z]/.test(ch)) {
          letters += ch;
          srcIndex += 1;
        } else {
          break;
        }
      }
      if (!letters) break;
      result += titleCaseMonthToken(letters);

      const groupComplete = letters.length >= (part.token === "MMM" ? 3 : 3);
      const next = structure[i + 1];
      if (groupComplete && next?.kind === "sep") {
        const hasMore = srcIndex < source.length;
        if (hasMore || !isDeleting) {
          result += next.value;
        }
      }
    }
  }

  return result;
};

/**
 * Build permissive parse variants for a display format so users can omit
 * leading zeros while typing (e.g. 1/2/2024 for dd/MM/yyyy).
 * Never corrupts textual MMM/MMMM into numeric M/MM.
 */
export const getFlexibleDateFormats = (dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  const primary = normalizeDateFormat(dateFormat);
  const variants = [primary];

  const withoutDayPad = primary.replace(/dd/g, "d");
  const withoutMonthPad = replaceNumericMonthToken(primary, "M");
  const withoutBothPad = replaceNumericMonthToken(withoutDayPad, "M");

  [withoutDayPad, withoutMonthPad, withoutBothPad].forEach((pattern) => {
    if (pattern && !variants.includes(pattern)) variants.push(pattern);
  });

  return variants;
};

/**
 * Compare typed segments with the parsed Date parts for the given pattern.
 * Supports numeric d/M/y tokens and textual MMM/MMMM months.
 * Tokenizes longest-first so MMM is never split into duplicate `month` groups.
 */
const doesTypedDateMatchParsed = (raw, pattern, parsed) => {
  // Longest tokens first — prevents MMM matching as MM then M (duplicate names).
  const tokenRegex = /yyyy|yy|MMMM|MMM|MM|dd|M|d/g;
  let regexSource = "";
  let lastIndex = 0;
  let tokenMatch;

  while ((tokenMatch = tokenRegex.exec(pattern)) !== null) {
    regexSource += pattern
      .slice(lastIndex, tokenMatch.index)
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    switch (tokenMatch[0]) {
      case "yyyy":
        regexSource += "(?<year>\\d{4})";
        break;
      case "yy":
        regexSource += "(?<year>\\d{2})";
        break;
      case "MMMM":
        regexSource += "(?<monthName>[A-Za-z]{3,9})";
        break;
      case "MMM":
        regexSource += "(?<monthName>[A-Za-z]{3})";
        break;
      case "MM":
        regexSource += "(?<month>\\d{2})";
        break;
      case "M":
        regexSource += "(?<month>\\d{1,2})";
        break;
      case "dd":
        regexSource += "(?<day>\\d{2})";
        break;
      case "d":
        regexSource += "(?<day>\\d{1,2})";
        break;
      default:
        break;
    }
    lastIndex = tokenRegex.lastIndex;
  }

  regexSource += pattern
    .slice(lastIndex)
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const match = new RegExp(`^${regexSource}$`, "i").exec(String(raw).trim());
  if (!match?.groups) return false;

  const {
    year: yearRaw,
    month: monthRaw,
    monthName: monthNameRaw,
    day: dayRaw,
  } = match.groups;

  if (yearRaw != null) {
    const year = Number(yearRaw);
    if (yearRaw.length === 2) {
      if (parsed.getFullYear() % 100 !== year) return false;
    } else if (parsed.getFullYear() !== year) {
      return false;
    }
  }

  if (monthRaw != null && parsed.getMonth() + 1 !== Number(monthRaw)) {
    return false;
  }

  if (monthNameRaw != null) {
    const abbr = titleCaseMonthToken(monthNameRaw).slice(0, 3);
    const monthIndex = MONTH_ABBREVIATIONS.findIndex((m) => m === abbr);
    if (monthIndex < 0 || parsed.getMonth() !== monthIndex) return false;
  }

  if (dayRaw != null && parsed.getDate() !== Number(dayRaw)) return false;

  return true;
};

/**
 * Parse typed input against the configured date format.
 * Accepts common unpadded variants, rejects invalid calendar dates,
 * and leaves incomplete values unparsed.
 */
export const parseTypedDate = (text, dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  if (text == null) return null;
  const raw = String(text).trim();
  if (!raw || !dateFormat) return null;

  const normalizedFormat = normalizeDateFormat(dateFormat);

  // Title-case month-name runs so "21 sep 2026" parses like "21 Sep 2026".
  const normalizedRaw = dateFormatHasMonthName(normalizedFormat)
    ? raw.replace(/[A-Za-z]+/g, (token) => titleCaseMonthToken(token))
    : raw;

  for (const pattern of getFlexibleDateFormats(normalizedFormat)) {
    const parsed = parse(normalizedRaw, pattern, new Date());
    if (!isValid(parsed)) continue;
    if (!doesTypedDateMatchParsed(normalizedRaw, pattern, parsed)) continue;
    return parsed;
  }

  return null;
};

/**
 * Convert a Date (or parseable value) to the canonical API string `YYYY-MM-DD`.
 * Use this whenever a date must be sent to an API / stored in form state for APIs.
 * Never send the pharmacy display format string to the API.
 */
export const toApiDateString = (value, displayFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  if (value instanceof Date) return formatDateValue(value);
  if (value == null || value === "") return "";

  const asDate =
    parseDateValue(value, [API_DATE_FORMAT, normalizeDateFormat(displayFormat)]) ||
    parseTypedDate(String(value), displayFormat);

  return asDate ? formatDateValue(asDate) : "";
};

/** Whether a date falls within optional inclusive min/max bounds (day precision). */
export const isDateWithinBounds = (date, minDate, maxDate) => {
  if (!(date instanceof Date) || !isValid(date)) return false;
  const day = startOfDay(date);
  if (minDate && isValid(minDate) && day < startOfDay(minDate)) return false;
  if (maxDate && isValid(maxDate) && day > startOfDay(maxDate)) return false;
  return true;
};

/**
 * Allow only characters that can appear while typing the configured format
 * (digits + separators present in the format pattern; letters when MMM/MMMM).
 */
export const isAllowedDateInputChar = (char, dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  if (!char) return true;
  if (/^\d$/.test(char)) return true;
  if (dateFormatHasMonthName(dateFormat) && /^[A-Za-z]$/.test(char)) return true;
  // Strip date tokens (including MMM/MMMM) to leave only separator characters.
  const separators = normalizeDateFormat(dateFormat)
    .replace(/yyyy|yy|MMMM|MMM|MM|dd|M|d/g, "");
  return separators.includes(char);
};

/**
 * Soft sanitize of pasted/typed text using characters allowed by the format.
 * Prefer maskDateInput for guided entry.
 */
export const sanitizeDateInput = (text, dateFormat = DEFAULT_PHARMACY_DISPLAY_DATE_FORMAT) => {
  if (text == null) return "";
  return String(text)
    .split("")
    .filter((char) => isAllowedDateInputChar(char, dateFormat))
    .join("");
};
